import { useGetFilterLabels } from '@app/components/data-table/hooks/use-get-filter-labels';
import PreviewSheet from '@app/components/preview/preview-sheet';
import { PreviewSheetBarChart } from '@app/components/preview/preview-sheet-bar-chart';
import PreviewSheetSection from '@app/components/preview/preview-sheet-chart-section';
import { useGetStationaryCombustionPreview } from '@shared/api/hooks/ccf/activities/stationary-combustion';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatNumber } from '@shared/lib/utils';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useCCF } from '../../ccf-context';
import { reportSlugs } from '../../slugs';

export default function StationaryCombustionPreviewSheet() {
  const [isOpen, setIsOpen] = useState(false);
  const { uuid: reportUuid, categories, scope3Supported } = useCCF();
  const [sites] = useQueryState('site', parseAsArrayOf(parseAsString));
  const [fuels] = useQueryState('fuel', parseAsArrayOf(parseAsString));
  const [period] = useQueryState('period', parseAsArrayOf(parseAsString));

  const { flatLabels: tags } = useGetFilterLabels(['site', 'fuel', 'period']);

  const category = categories?.find(
    (item) => item.slug === reportSlugs.stationaryCombustion
  );

  const { data: stationaryCombustionPreviewData, status } =
    useGetStationaryCombustionPreview({
      companyReportUuid: reportUuid!,
      params: {
        sites: sites || undefined,
        fuels: fuels || undefined,
        time_periods: period || undefined,
      },
      enabled: isOpen,
    });

  const totalEmissionsTonnes =
    stationaryCombustionPreviewData?.totalEmissionsTonnes;

  const totalScope3EmissionsTonnes =
    stationaryCombustionPreviewData?.totalScope3EmissionsTonnes;

  const emissionsBySite = stationaryCombustionPreviewData?.emissionsBySite?.map(
    (site) => {
      return {
        site: site.siteName,
        emissions: site.totalSiteEmissionsTonnes,
        fill: 'var(--chart-3)',
      };
    }
  );

  const highestEmissionsActivities =
    stationaryCombustionPreviewData?.highestEmissionActivities?.map(
      (activity) => {
        return {
          fuel: activity.name,
          emissions: activity.emissionsTonnes,
          fill: 'var(--chart-3)',
        };
      }
    );

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-3))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <PreviewSheet
      title={`Scope ${category?.scope} - ${category?.name}`}
      status={status}
      metrics={[
        {
          title: 'Scope 1 emissions',
          value: formatNumber(totalEmissionsTonnes!),
          unit: 'tCO2e',
        },
        ...(scope3Supported
          ? [
              {
                title: 'Scope 3 emissions',
                value: formatNumber(totalScope3EmissionsTonnes!),
                unit: 'tCO2e',
              },
            ]
          : []),
      ]}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      tags={tags}
    >
      <PreviewSheetSection title="Emissions by site">
        <PreviewSheetBarChart
          nameKey="site"
          chartData={emissionsBySite}
          chartConfig={chartConfig}
        />
      </PreviewSheetSection>
      <PreviewSheetSection title="Highest emission fuels">
        <PreviewSheetBarChart
          nameKey="fuel"
          chartData={highestEmissionsActivities}
          chartConfig={chartConfig}
        />
      </PreviewSheetSection>
    </PreviewSheet>
  );
}
